import { ChangeEvent } from 'react';
import NumberEdit from '../../../../molecules/NumberEdit';
import SelectTextField from '../../../../molecules/SelectTextField';
import DaysChipsInput from './DaysChipsInput';
import type { FieldError } from '../../../../../services/collectionApi';
import {
    CollectionPhase,
    NON_BUSINESS_DAY_LABEL,
    NonBusinessDayRule,
    optionsOf,
    OverdueSchedule,
    PreventiveSchedule,
    SCHEDULE_KEY,
} from '../collectionModel';

interface PhaseScheduleFieldsProps {
    phase: CollectionPhase;
    preventive: PreventiveSchedule | null;
    overdue: OverdueSchedule | null;
    fieldErrors: FieldError[];
    onPreventiveChange: (schedule: PreventiveSchedule) => void;
    onOverdueChange: (schedule: OverdueSchedule) => void;
}

const NON_BUSINESS_DAY_FIELD = 'Se cair em fim de semana ou feriado';

const errorOf = (fieldErrors: FieldError[], ...fields: string[]): string | undefined =>
    fieldErrors.find(e => fields.includes(e.fieldName))?.message;

const daysOf = (event: ChangeEvent<HTMLInputElement>): number | null =>
    event.target.value === '' ? null : Number(event.target.value);

const PhaseScheduleFields = ({ phase, preventive, overdue, fieldErrors, onPreventiveChange, onOverdueChange }: PhaseScheduleFieldsProps) => {
    const prefix = `schedules.${SCHEDULE_KEY[phase]}`;
    const ruleError = errorOf(fieldErrors, `${prefix}.nonBusinessDayRule`);
    const daysBeforeError = errorOf(fieldErrors, `${prefix}.daysBefore`);
    const missingError = errorOf(fieldErrors, prefix);
    const nonBusinessItems = optionsOf(NON_BUSINESS_DAY_LABEL);
    const fieldId = (field: string) => `${SCHEDULE_KEY[phase]}-${field}`;

    return (
        <div className="mb-4 rounded-lg bg-slate-50 px-4 py-3 dark:bg-slate-800/60">
            <p className="mb-4 text-xs font-semibold text-gray-700 dark:text-gray-200">Quando cobrar</p>
            <div className="flex flex-wrap items-end gap-6">
                {phase === 'PREVENTIVE' && preventive && (
                    <>
                        <div className="min-w-[260px]">
                            <DaysChipsInput label="Dias antes do vencimento" days={preventive.daysBefore} suffix="dias"
                                onChange={daysBefore => onPreventiveChange({ ...preventive, daysBefore })} />
                            {daysBeforeError && <p className="mt-2 text-sm text-red-600">{daysBeforeError}</p>}
                        </div>
                        <SelectTextField id={fieldId('nonBusinessDayRule')} label={NON_BUSINESS_DAY_FIELD} className="w-72"
                            items={nonBusinessItems} hideEmptyOption value={preventive.nonBusinessDayRule} errorMessage={ruleError}
                            onChange={e => onPreventiveChange({ ...preventive, nonBusinessDayRule: e.target.value as NonBusinessDayRule })} />
                    </>
                )}
                {phase === 'OVERDUE' && overdue && (
                    <>
                        <NumberEdit id={fieldId('firstAfterDays')} label="1ª cobrança após (dias)" className="w-44"
                            value={overdue.firstAfterDays ?? ''} errorMessage={errorOf(fieldErrors, `${prefix}.firstAfterDays`)}
                            onChange={e => onOverdueChange({ ...overdue, firstAfterDays: daysOf(e) })} />
                        <NumberEdit id={fieldId('everyDays')} label="Depois, a cada (dias)" className="w-44"
                            value={overdue.everyDays ?? ''} errorMessage={errorOf(fieldErrors, `${prefix}.everyDays`)}
                            onChange={e => onOverdueChange({ ...overdue, everyDays: daysOf(e) })} />
                        <SelectTextField id={fieldId('nonBusinessDayRule')} label={NON_BUSINESS_DAY_FIELD} className="w-72"
                            items={nonBusinessItems} hideEmptyOption value={overdue.nonBusinessDayRule} errorMessage={ruleError}
                            onChange={e => onOverdueChange({ ...overdue, nonBusinessDayRule: e.target.value as NonBusinessDayRule })} />
                    </>
                )}
            </div>
            {missingError && <p className="mt-2 text-xs text-red-600">{missingError}</p>}
        </div>
    );
};

export default PhaseScheduleFields;
